import React, { createContext, useContext, useReducer, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import useLocalStorage from '../hooks/useLocalStorage';
import { NOTIFICATION_TYPES } from '../constants';
import config from '../config/app.config';
import { setAuthTokenProvider } from '../config/apiClient';
import { setGlobalTokenProvider } from '../config/setupAxiosInterceptors';
import '../config/setupAxiosInterceptors'; // Importar para ejecutar la configuración

// Estado inicial
const initialState = {
  // UI State
  loading: false,
  sidebarOpen: window.innerWidth >= 1024,
  sidebarCollapsed: false,
  theme: 'light',
  notifications: [],
  
  // App State
  appConfig: config,
  isOnline: navigator.onLine,
  
  // User Preferences
  preferences: {
    language: 'es',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    itemsPerPage: 10
  }
};

// Tipos de acciones
const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  TOGGLE_SIDEBAR_COLLAPSE: 'TOGGLE_SIDEBAR_COLLAPSE',
  SET_THEME: 'SET_THEME',
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  SET_ONLINE_STATUS: 'SET_ONLINE_STATUS',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  RESET_STATE: 'RESET_STATE'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case actionTypes.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };
    
    case actionTypes.TOGGLE_SIDEBAR_COLLAPSE:
      return { ...state, sidebarCollapsed: !state.sidebarCollapsed };
    
    case actionTypes.SET_THEME:
      return { ...state, theme: action.payload };
    
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [...state.notifications, {
          id: Date.now() + Math.random(),
          timestamp: new Date(),
          ...action.payload
        }]
      };
    
    case actionTypes.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
    
    case actionTypes.CLEAR_NOTIFICATIONS:
      return { ...state, notifications: [] };
    
    case actionTypes.SET_ONLINE_STATUS:
      return { ...state, isOnline: action.payload };
    
    case actionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: { ...state.preferences, ...action.payload }
      };
    
    case actionTypes.RESET_STATE:
      return { ...initialState };
    
    default:
      return state;
  }
};

// Crear contexto
const AppContext = createContext();

// Provider del contexto
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const auth = useAuth();
  
  // Persistir preferencias
  const [persistedPreferences, setPersistedPreferences] = useLocalStorage(
    'app_preferences', 
    state.preferences
  );
  
  const [persistedTheme, setPersistedTheme] = useLocalStorage(
    'app_theme', 
    state.theme
  );

  // Sincronizar preferencias persistidas
  useEffect(() => {
    if (persistedPreferences) {
      dispatch({
        type: actionTypes.UPDATE_PREFERENCES,
        payload: persistedPreferences
      });
    }
  }, [persistedPreferences]);

  // Sincronizar tema persistido
  useEffect(() => {
    if (persistedTheme) {
      dispatch({
        type: actionTypes.SET_THEME,
        payload: persistedTheme
      });
    }
  }, [persistedTheme]);

  // Monitorear estado de conexión
  useEffect(() => {
    const handleOnline = () => dispatch({
      type: actionTypes.SET_ONLINE_STATUS,
      payload: true
    });
    
    const handleOffline = () => dispatch({
      type: actionTypes.SET_ONLINE_STATUS,
      payload: false
    });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Configurar el proveedor de token para el cliente API
  useEffect(() => {
    if (auth.getToken) {
      console.log('🔧 Configurando proveedor de token para API client');
      setAuthTokenProvider(auth.getToken);
      
      console.log('🔧 Configurando proveedor de token global para axios');
      setGlobalTokenProvider(auth.getToken);
    }
  }, [auth.getToken]);

  // Inicializar autenticación automáticamente al cargar la aplicación
  useEffect(() => {
    const initializeAuth = async () => {
      if (!auth.initialized && !auth.loading) {
        console.log('🚀 Inicializando autenticación automáticamente...');
        try {
          await auth.initializeKeycloak();
          console.log('✅ Autenticación inicializada automáticamente');
        } catch (error) {
          console.error('❌ Error inicializando autenticación:', error);
        }
      }
    };

    initializeAuth();
    // Solo usar las propiedades específicas para evitar bucles infinitos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.initialized, auth.loading]);

  // Acciones
  const actions = {
    setLoading: (loading) => dispatch({
      type: actionTypes.SET_LOADING,
      payload: loading
    }),

    toggleSidebar: () => dispatch({
      type: actionTypes.TOGGLE_SIDEBAR
    }),

    toggleSidebarCollapse: () => dispatch({
      type: actionTypes.TOGGLE_SIDEBAR_COLLAPSE
    }),

    setTheme: (theme) => {
      dispatch({ type: actionTypes.SET_THEME, payload: theme });
      setPersistedTheme(theme);
    },

    showNotification: (notification) => {
      dispatch({
        type: actionTypes.ADD_NOTIFICATION,
        payload: {
          type: NOTIFICATION_TYPES.INFO,
          autoClose: true,
          duration: 5000,
          ...notification
        }
      });
    },

    showSuccess: (message, options = {}) => {
      dispatch({
        type: actionTypes.ADD_NOTIFICATION,
        payload: {
          type: NOTIFICATION_TYPES.SUCCESS,
          message,
          autoClose: true,
          duration: 4000,
          ...options
        }
      });
    },

    showError: (message, options = {}) => {
      dispatch({
        type: actionTypes.ADD_NOTIFICATION,
        payload: {
          type: NOTIFICATION_TYPES.ERROR,
          message,
          autoClose: false,
          ...options
        }
      });
    },

    showWarning: (message, options = {}) => {
      dispatch({
        type: actionTypes.ADD_NOTIFICATION,
        payload: {
          type: NOTIFICATION_TYPES.WARNING,
          message,
          autoClose: true,
          duration: 6000,
          ...options
        }
      });
    },

    removeNotification: (id) => dispatch({
      type: actionTypes.REMOVE_NOTIFICATION,
      payload: id
    }),

    clearNotifications: () => dispatch({
      type: actionTypes.CLEAR_NOTIFICATIONS
    }),

    updatePreferences: (preferences) => {
      dispatch({
        type: actionTypes.UPDATE_PREFERENCES,
        payload: preferences
      });
      setPersistedPreferences({
        ...state.preferences,
        ...preferences
      });
    },

    resetState: () => dispatch({
      type: actionTypes.RESET_STATE
    })
  };

  const value = {
    // Estado
    ...state,
    
    // Autenticación
    auth,
    
    // Theme (usando el estado interno del reducer)
    theme: state.theme,
    toggleTheme: () => {
      const newTheme = state.theme === 'light' ? 'dark' : 'light';
      actions.setTheme(newTheme);
    },
    
    // Notifications (usando las acciones del reducer)
    showSuccess: actions.showSuccess,
    showError: actions.showError,
    showInfo: actions.showNotification,
    showWarning: actions.showWarning,
    notifications: state.notifications,
    clearNotification: actions.removeNotification,
    clearAllNotifications: actions.clearNotifications,
    
    // Acciones
    ...actions
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Hook para usar el contexto
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp debe ser usado dentro de AppProvider');
  }
  return context;
};

export default AppContext; 